import { observer } from "mobx-react-lite";
import type {
  ExonnHubCheckboxFilterDefinition,
  ExonnHubCheckboxFilterItem,
} from "src/admin/modules/b2b-gate-order/components/ExonnHub/ExonnHub.types.ts";
import { useExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubStore.ts";
import { unique } from "@exonn/utils";
import { isCheckboxFilterModel } from "src/admin/modules/b2b-gate-order/components/ExonnHub/ExonnHub.utils.ts";
import { type ChangeEvent, useState } from "react";
import { Collapsible } from "src/components/Collapsible/Collapsible.tsx";
import * as styles from "./CheckboxFilter.style.ts";
import { Flex } from "src/components/Flex.tsx";
import { CheckboxV2 } from "src/components/CheckboxV2/CheckboxV2.tsx";
import { BaseInputV2Adornment } from "src/components/BaseInputV2/BaseInputV2Adornment.tsx";
import { SearchIcon } from "src/lib/ui-icon-v2";
import { BaseInputV2 } from "src/components/BaseInputV2/BaseInputV2.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";

export const CheckboxFilter = observer(
  ({ filter }: { filter: ExonnHubCheckboxFilterDefinition }) => {
    const store = useExonnHubStore();
    const [search, setSearch] = useState("");
    const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);

    const filterModels = store.filterModels;
    const collapsedFilters = store.collapsedFilters;

    const changeCollapsed = (collapsed: boolean) => {
      const nextCollapsedFilters = !collapsed
        ? collapsedFilters.filter((filterName) => filterName !== filter.name)
        : unique([...collapsedFilters, filter.name]);
      store.setCollapsedFilters(nextCollapsedFilters);
    };
    const model = filterModels
      .filter(isCheckboxFilterModel)
      .find((model) => model.name === filter.name) || {
      name: filter.name,
      query_param: filter.query_param,
      type: filter.type,
      values: [],
    };
    const createCheckboxHandler =
      (item: ExonnHubCheckboxFilterItem) =>
      (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.currentTarget.checked;

        const nextValues = checked
          ? unique([...model.values, item.value])
          : model.values.filter((value) => value !== item.value);

        if (nextValues.length) {
          const nextFilterModels = [
            ...filterModels.filter((f) => f.name !== filter.name),
            {
              ...model,
              values: nextValues,
            },
          ];
          store.setFilterModels(nextFilterModels);
        } else {
          store.setFilterModels(
            filterModels.filter((f) => f.name !== filter.name),
          );
        }
      };

    const showSearch = filter.items.length > 15 || !!search;

    const filteredItems = filter.items.filter((item) => {
      return item.label.toLowerCase().indexOf(search.toLowerCase()) !== -1;
    });
    const handleClearSearch = () => {
      setSearch("");
    };
    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    };
    return (
      <Collapsible
        key={filter.name}
        onCollapsedChange={changeCollapsed}
        defaultCollapsed={collapsedFilters.includes(filter.name)}
        collapsed={collapsedFilters.includes(filter.name)}
        title={t(`filter.${filter.name}.label`, {
          defaultValue: filter.label,
        })}
      >
        {showSearch && (
          <div css={styles.searchContainer}>
            <BaseInputV2
              value={search}
              onChange={handleSearchChange}
              startAdornment={
                <BaseInputV2Adornment>
                  <SearchIcon />
                </BaseInputV2Adornment>
              }
              endAdornment={
                search && (
                  <BaseInputV2Adornment
                    isAction={true}
                    onClick={handleClearSearch}
                  >
                    <i className="icon-15-clear_x" />
                  </BaseInputV2Adornment>
                )
              }
            />
          </div>
        )}

        <div css={styles.checkboxGroupContent}>
          <Flex flexDirection="column" gap={"10px"}>
            {filteredItems.map((item) => (
              <CheckboxV2
                key={item.value}
                checked={(model?.values || []).includes(item.value)}
                label={item.label}
                value={item.value}
                onChange={createCheckboxHandler(item)}
              />
            ))}
          </Flex>
        </div>
      </Collapsible>
    );
  },
);
